export const COMMUNITY_FEATURE_FLAGS = [
    {
        key: 'comments',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Master comment-system flag. When off, every /comments + /admin/comments route returns 503-equivalent COMMENTS_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'comments.guest',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow anonymous (guest) comments. When off, the API requires a session and rejects the `guest` payload on POST.',
        schemaVersion: 1,
    },
    {
        key: 'comments.attachments',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow file attachments on comments. When off, the API rejects non-empty `attachments` arrays with 422.',
        schemaVersion: 1,
    },
    {
        key: 'comments.reactions',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow like/dislike reactions on comments. When off, POST /comments/:id/reactions returns 422 COMMENT_REACTIONS_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'comments.dislikes',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow `kind=dislike` reactions specifically. Plan.md III.2 marks dislikes "optional"; default off keeps the surface positive-only until an admin opts in.',
        schemaVersion: 1,
    },
    {
        key: 'comments.reports',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow abuse-report submissions. When off, POST /comments/:id/reports returns 422 COMMENT_REPORTS_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'comments.spam-akismet',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Run incoming comments through Akismet for spam classification. When off, the service skips the external probe.',
        schemaVersion: 1,
    },
    {
        key: 'comments.subscriptions',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow per-user subscriptions to new-comment alerts on an article (plan.md III.4).',
        schemaVersion: 1,
    },
    {
        key: 'comments.export',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'content/comments',
        description: 'Allow admin export of comments to CSV/JSON. When off, /admin/comments/export returns 422 COMMENT_EXPORT_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'forum',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Master forum flag. When off, every /forum route returns FORUM_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'forum.votes',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow voting on forum posts. When off, POST /forum/posts/:id/votes returns FORUM_VOTES_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'forum.accepted-answer',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow question thread authors and moderators to mark one post as the accepted answer.',
        schemaVersion: 1,
    },
    {
        key: 'forum.subscriptions',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow users to subscribe to thread reply notifications.',
        schemaVersion: 1,
    },
    {
        key: 'forum.ai-summary',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Generate an independent forum thread summary for list rows and detail-page subtitles. Uses the configured AI provider when available, with a local fallback.',
        schemaVersion: 1,
    },
    {
        key: 'forum.auto-translation',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Generate AI translation snapshots for forum threads and replies in the background. Requires AI_API_KEY; disabled instances skip subscriber work.',
        schemaVersion: 1,
    },
    {
        key: 'forum.cross-group',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow Everyone-scoped site-wide forum threads.',
        schemaVersion: 1,
    },
    {
        key: 'forum.kind.poll',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow poll thread kind.',
        schemaVersion: 1,
    },
    {
        key: 'forum.kind.ama',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow AMA thread kind.',
        schemaVersion: 1,
    },
    {
        key: 'forum.kind.event-rsvp',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/forum',
        description: 'Allow Event-RSVP thread kind.',
        schemaVersion: 1,
    },
    {
        key: 'engagement',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/engagement',
        description: 'Master engagement flag for group task, point ledger, integrity review, and reward routes.',
        schemaVersion: 1,
    },
    {
        key: 'engagement.points',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/engagement',
        description: 'Allow group owners to enable point pools and task-based point earning.',
        schemaVersion: 1,
    },
    {
        key: 'engagement.rewards',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/engagement',
        description: 'Allow group owners to publish reward items and users to redeem points.',
        schemaVersion: 1,
    },
    {
        key: 'engagement.integrity-review',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/engagement',
        description: 'Enable held point entries and owner review queues for suspicious activity.',
        schemaVersion: 1,
    },
    {
        key: 'surveys',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/surveys',
        description: 'Master survey flag. Off -> every /surveys route returns SURVEYS_DISABLED.',
        schemaVersion: 1,
    },
    {
        key: 'surveys.conditional-logic',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/surveys',
        description: 'Allow branchingRules to affect the composer and future runtime question flow. V1 stores the rules and validates their shape.',
        schemaVersion: 1,
    },
    {
        key: 'surveys.file-upload-question',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/surveys',
        description: 'Allow the file-upload question kind. Off -> create/update refuses file-upload questions.',
        schemaVersion: 1,
    },
    {
        key: 'surveys.public-results',
        defaultValue: false,
        scope: 'global',
        ownerModule: 'community/surveys',
        description: 'Allow public result views for surveys that opt in. V1 keeps owner/manager analytics private unless this flag is enabled.',
        schemaVersion: 1,
    },
    {
        key: 'surveys.iframe-embed',
        defaultValue: true,
        scope: 'global',
        ownerModule: 'community/surveys',
        description: 'Allow survey embed metadata and future iframe snippets. The backend stores article x survey embed rows.',
        schemaVersion: 1,
    },
];
//# sourceMappingURL=registry-community.js.map